import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons"
import { Button, Flex, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { generateDocumentTitle } from "../../constants/document-title.ts"
import { RoutesPaths } from "../../constants/routes.ts"
import { BigPictureDiagram } from "./components/story/BigPictureDiagram.tsx"
import { ConnectedWorkflowDiagram } from "./components/story/ConnectedWorkflowDiagram.tsx"
import { ExploreStepPanel } from "./components/story/ExploreStepPanel.tsx"
import { PlatformPillarsGrid } from "./components/story/PlatformPillarsGrid.tsx"
import { StoryStepShell } from "./components/story/StoryStepShell.tsx"
import { PRODUCT_STORY_ID } from "./content/product-story.ts"
import { useGuideProgress } from "./hooks/use-guide-progress.ts"
import { useGuideTour } from "./tour/GuideTourContext.tsx"
import { useProductTour } from "./tour/ProductTourContext.tsx"

const { Text } = Typography

const STEPS = [
  {
    eyebrowKey: "guide.story.steps.bigPicture.eyebrow",
    titleKey: "guide.story.steps.bigPicture.title",
    subtitleKey: "guide.story.steps.bigPicture.subtitle",
    calloutKey: "guide.story.steps.bigPicture.callout",
    render: () => <BigPictureDiagram />
  },
  {
    eyebrowKey: "guide.story.steps.workflow.eyebrow",
    titleKey: "guide.story.steps.workflow.title",
    subtitleKey: "guide.story.steps.workflow.subtitle",
    calloutKey: "guide.story.steps.workflow.callout",
    render: () => <ConnectedWorkflowDiagram />
  },
  {
    eyebrowKey: "guide.story.steps.pillars.eyebrow",
    titleKey: "guide.story.steps.pillars.title",
    subtitleKey: "guide.story.steps.pillars.subtitle",
    calloutKey: "guide.story.steps.pillars.callout",
    render: () => <PlatformPillarsGrid />
  },
  {
    eyebrowKey: "guide.story.steps.explore.eyebrow",
    titleKey: "guide.story.steps.explore.title",
    subtitleKey: "guide.story.steps.explore.subtitle",
    render: () => <ExploreStepPanel />
  }
] as const

export const GuideProductStoryPage = () => {
  const { t } = useTranslation()
  const { setLessonComplete } = useGuideProgress()
  const { start: startProductTour } = useProductTour()
  const { activeTour: activeGuideTour, skip: skipGuideTour } = useGuideTour()
  const [stepIndex, setStepIndex] = useState(0)

  useEffect(() => {
    generateDocumentTitle(t("guide.story.documentTitle"))
  }, [t])

  const isLastStep = stepIndex === STEPS.length - 1
  const step = STEPS[stepIndex]

  const handleNext = () => {
    if (!isLastStep) {
      setStepIndex((index) => index + 1)
      return
    }
    // "Finished" happens naturally by reaching the end, not from a separate
    // Mark as complete action.
    setLessonComplete(PRODUCT_STORY_ID)
    // The two tour engines don't guard against each other — starting the
    // Product Tour while a lesson tour is active would mount both overlays
    // at once (same fixed z-index, two sets of Next/Back/Skip controls).
    if (activeGuideTour) skipGuideTour()
    startProductTour()
  }

  const handlePrevious = () => setStepIndex((index) => Math.max(0, index - 1))

  return (
    <Flex vertical gap={32} className="mx-auto max-w-4xl pb-10">
      <Flex justify="space-between" align="center">
        <Link
          to={RoutesPaths.guide.home}
          className="flex items-center gap-1 text-sm text-gray-500"
        >
          <ArrowLeftOutlined /> {t("guide.story.back")}
        </Link>
        <Text type="secondary" className="text-xs font-semibold">
          {t("guide.story.stepCounter", {
            current: stepIndex + 1,
            total: STEPS.length
          })}
        </Text>
      </Flex>

      <StoryStepShell
        eyebrowKey={step.eyebrowKey}
        titleKey={step.titleKey}
        subtitleKey={step.subtitleKey}
        calloutKey={"calloutKey" in step ? step.calloutKey : undefined}
      >
        {step.render()}
      </StoryStepShell>

      <Flex
        justify="space-between"
        align="center"
        className="border-t border-border pt-4"
      >
        <Button disabled={stepIndex === 0} onClick={handlePrevious}>
          {t("guide.story.previous")}
        </Button>
        <Button
          type="primary"
          onClick={handleNext}
          icon={<ArrowRightOutlined />}
          iconPosition="end"
        >
          {isLastStep ? t("guide.story.startTour") : t("guide.story.next")}
        </Button>
      </Flex>
    </Flex>
  )
}
